import {
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
  NavigationEnd,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import { Subscription } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
import { HttpCancelService } from './services/http-cancel.service';
import { LoaderComponent } from './components/layout/loader/loader.component';
import { environment } from '../environments/environment';
import { version } from '../../package.json';
import { UtilityService } from './services/utility.service';
import { MaintenanceConfig } from './models/maintenance.model';
import { CachingService } from './services/caching.service';
import { cachingKeys } from './constants/caching-keys';
import { routes as appRoutes } from './constants/routes';
import { ChatEntryBarComponent } from './components/utility/chatbot/chat-entry-bar/chat-entry-bar.component';
import { ChatDockComponent } from './components/utility/chatbot/chat-dock/chat-dock.component';
import { ChatStateService } from './services/chat-state.service';
import { PosthogService } from './services/posthog.service';

declare const gtag: Function;

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    LoaderComponent,
    ChatEntryBarComponent,
    ChatDockComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  showLangDropdown!: boolean;
  initialPageLoad = true;
  maintenanceData!: MaintenanceConfig;
  isMaintenanceInfoAcknowledged: boolean = false;
  private routeChangeSubs!: Subscription;

  isDockOpen = false;
  dockWidth = 0;
  isLoadingPage = false;
  isDesktopLayout = typeof window !== 'undefined' ? window.innerWidth >= 992 : true;
  private dockOpenSub!: Subscription;
  private dockWidthSub!: Subscription;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: string,
    private router: Router,
    private httpCancelService: HttpCancelService,
    private utilityService: UtilityService,
    private cachingService: CachingService,
    private chatState: ChatStateService,
    private posthogService: PosthogService
  ) {
    this.checkMaintenance();
  }

  ngOnInit() {
    this.isLoadingPage =
      this.router.url.includes(`/${appRoutes.account}/${appRoutes.authRedirectPage}`) ||
      this.router.url.includes(`/${appRoutes.account}/${appRoutes.login}/${appRoutes.sso}`);
    this.listenToRouteChange();
    this.setGoogleSiteVerification();
    this.dockOpenSub = this.chatState.isDockOpen$.subscribe((isOpen) => {
      this.isDockOpen = isOpen;
    });
    this.dockWidthSub = this.chatState.dockWidth$.subscribe((width) => {
      this.dockWidth = width;
    });
    // window.addEventListener('offline', () => {
    //   this.toaster.error('No internet connection');
    // });
  }

  @HostListener('window:resize')
  onResize(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.isDesktopLayout = window.innerWidth >= 992;
    }
  }

  closeDock(): void {
    this.chatState.closeDock();
  }

  private listenToRouteChange() {
    let previousUrl: string;

    this.routeChangeSubs = this.router.events
      .pipe(
        tap((event) => {
          if (event instanceof NavigationStart) {
            previousUrl = this.router.url;
          }
        }),
        filter((event) => event instanceof NavigationEnd),
      )
      .subscribe((event: any) => {
        const { urlAfterRedirects } = <NavigationEnd>event;
        this.handleArtefactFilters(event.url);
        this.showLangDropdown = urlAfterRedirects === '/';
        this.isLoadingPage =
          urlAfterRedirects.includes(`/${appRoutes.account}/${appRoutes.authRedirectPage}`) ||
          urlAfterRedirects.includes(`/${appRoutes.account}/${appRoutes.login}/${appRoutes.sso}`);
        this.logGoogleAnalytics(urlAfterRedirects);
        this.posthogService.trackPageView(urlAfterRedirects);

        if (this.initialPageLoad) {
          this.initialPageLoad = false;
          return;
        }

        if (urlAfterRedirects === previousUrl) {
          return;
        }

        this.httpCancelService.cancelAllRequests();
      });
  }

  setGoogleSiteVerification() {
    if (environment?.production && isPlatformBrowser(this.platformId)) {
      const meta = document?.createElement('meta');
      meta.name = 'google-site-verification';
      meta.content = environment?.googleSiteVerification;
      document.head.appendChild(meta);
    }
  }

  private logGoogleAnalytics(url: string) {
    if (isPlatformServer(this.platformId) || !environment.production) {
      return;
    }

    gtag('event', 'page_view', {
      page_location: this.document.location.href,
      page_path: url,
    });
  }

  handleArtefactFilters(url: string) {
    const routes = [
      {
        path: ['/home/datasets/all', '/home/datasets'],
        detailPath: '/home/datasets/details/',
        pageNoKey: 'datasetCurrpage',
        searchKey: 'datasetSearchText',
      },
      {
        path: ['/home/models/all', '/home/models'],
        detailPath: '/home/models/details/',
        pageNoKey: 'modelCurrPage',
        searchKey: 'modelSearchText',
      },
      {
        path: ['/home/use-cases/all', '/home/use-cases'],
        detailPath: '/home/use-cases/details/',
        pageNoKey: 'usecaseCurrpage',
        searchKey: 'usecaseSearchText',
      },
      {
        path: ['/home/organizations/all', '/home/organizations'],
        detailPath: 'home/public-profile/org',
        pageNoKey: 'organizationCurrpage',
        searchKey: 'organizationSearchText',
      },
      {
        path: ['/home/model-tryout/all', '/home/model-tryout'],
        detailPath: '/home/model-tryout/details/',
        pageNoKey: 'modeltryoutCurrPage',
        searchKey: 'modeltryoutSearchText',
      },
    ];

    routes.forEach((route) => {
      const pathMatched = route.path.some((path) => url === path);
      if (!pathMatched && !url.startsWith(route.detailPath)) {
        this.utilityService.updateStateProperty(route.pageNoKey, 1);
        this.utilityService.updateStateProperty(route.searchKey, '');
      }
    });
  }

  checkMaintenance = (): void => {
    const maintenanceData: MaintenanceConfig | null =
      this.cachingService.getCache(cachingKeys.maintenance);

    if (!maintenanceData) {
      return;
    }

    this.maintenanceData = maintenanceData;
    this.isMaintenanceInfoAcknowledged = false;
  };

  ngOnDestroy() {
    this.routeChangeSubs.unsubscribe();
    this.dockOpenSub?.unsubscribe();
    this.dockWidthSub?.unsubscribe();
  }
}
